import { useState } from "react";
import { useFirebaseAuth } from "./FirebaseAuth";

interface useFetchProps {
  url: string;
  method?: string;
  payload?: string;
}

interface useFetchResponse {
  data?: any | null;
  error?: any | null;
  statusCode?: number | null;
}

interface FetchOptions {
  url?: string;
  method?: string;
  headers?: Record<string, string>;
  body?: any;
}

export const useAuthenticatedFetch = ({ url, method }: useFetchProps) => {
  const user = useFirebaseAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [res, setRes] = useState<useFetchResponse>({
    data: null,
    error: null,
    statusCode: null,
  });

  const callAPI = async (optionsOrPayload?: string | FetchOptions) => {
    if (user) {
      try {
        setIsLoading(true);

        let finalUrl = url;
        let finalMethod = method;
        let finalHeaders: Record<string, string> = {
          authorization: `Bearer ${await user.getIdToken()}`,
          "Content-type": "application/json",
        };
        let finalBody: string | undefined;

        if (typeof optionsOrPayload === "string") {
          // Backwards compatible mode
          finalBody = optionsOrPayload;
        } else if (optionsOrPayload) {
          // New options mode
          if (optionsOrPayload.url) finalUrl = optionsOrPayload.url;
          if (optionsOrPayload.method) finalMethod = optionsOrPayload.method;
          if (optionsOrPayload.headers) {
            finalHeaders = { ...finalHeaders, ...optionsOrPayload.headers };
          }
          if (optionsOrPayload.body) {
            finalBody = JSON.stringify(optionsOrPayload.body);
          }
        }

        const response = await fetch(finalUrl, {
          method: finalMethod,
          headers: finalHeaders,
          body: finalBody,
        });

        if (response.status === 200) {
          setRes({
            data: await response.json(),
            error: null,
            statusCode: 200,
          });
        } else {
          setRes((prevState) => ({
            ...prevState,
            statusCode: response.status,
          }));
        }
      } catch (error) {
        setRes({ data: null, error, statusCode: null });
      }
      setIsLoading(false);
    } else {
      setRes((prevState) => ({
        ...prevState,
        error: "You are not authorized",
      }));
    }
  };

  return { res, callAPI, isLoading };
};

export const useFetch = ({ url, method }: useFetchProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const user = useFirebaseAuth();

  const [res, setRes] = useState<useFetchResponse>({
    data: null,
    error: null,
    statusCode: null,
  });

  const callAPI = async (payload?: string) => {
    try {
      setIsLoading(true);
      const response = await fetch(url, {
        method,
        headers: {
          authorization: user ? `Bearer ${await user.getIdToken()}` : "",
          "Content-type": "application/json",
        },
        body: payload,
      });
      if (response.status === 200) {
        return setRes({
          data: await response.json(),
          error: null,
          statusCode: 200,
        });
      } else {
        setRes((prevState) => ({
          ...prevState,
          statusCode: response.status,
        }));
      }
    } catch (error) {
      setRes({ data: null, error, statusCode: null });
    }
    setIsLoading(false);
  };

  return { res, callAPI, isLoading };
};
